import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelText as _vModelText, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "text-sm text-red-500"
}
const _hoisted_2 = { class: "flex flex-col gap-1 text-sm" }
const _hoisted_3 = ["value"]
const _hoisted_4 = { class: "flex flex-col gap-1 text-sm" }
const _hoisted_5 = { class: "flex flex-col gap-1 text-sm" }
const _hoisted_6 = { class: "flex flex-col gap-1 text-sm" }
const _hoisted_7 = { class: "flex flex-col gap-2 text-sm" }
const _hoisted_8 = ["onUpdate:modelValue", "aria-label"]
const _hoisted_9 = ["onUpdate:modelValue", "aria-label"]
const _hoisted_10 = ["onUpdate:modelValue", "aria-label"]
const _hoisted_11 = ["value"]
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { class: "flex flex-col gap-2 text-sm" }
const _hoisted_14 = { class: "flex flex-col gap-2" }
const _hoisted_15 = {
  key: 0,
  class: "flex items-start gap-3"
}
const _hoisted_16 = ["src"]
const _hoisted_17 = {
  key: 1,
  class: "flex flex-col gap-1"
}
const _hoisted_18 = { class: "flex gap-3" }
const _hoisted_19 = ["disabled"]

import type { StrongRef, UsesCategoryRecord, UsesItemRecord } from '#shared/cms/records'
import { community } from '#shared/lex'
import { ref } from 'vue'
import { COMMUNITY_IMAGE_MAX_BYTES, pdsBlobUrl } from '#shared/cms/blob'

type UsesItemValue = Omit<UsesItemRecord, '$type'>

interface UsesCategoryEntry {
  rkey: string
  uri: string
  cid: string
  value: UsesCategoryRecord
}


export default /*@__PURE__*/_defineComponent({
  __name: 'UsesItemForm',
  props: {
    initial: {},
    submitLabel: {}
  },
  emits: ["submit"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const { data: categories } = useAdminFetch<UsesCategoryEntry[]>('/api/admin/uses-categories', { default: () => [] })

const { defs } = community.lexicon.app

const LINK_ROLES = [
  { value: '', label: 'No role' },
  { value: defs.linkRoleWebsite.value, label: 'Website' },
  { value: defs.linkRoleSourceCode.value, label: 'Source code' },
  { value: defs.linkRoleDocs.value, label: 'Docs' },
  { value: defs.linkRoleSupport.value, label: 'Support' },
]

const { public: publicConfig } = useRuntimeConfig()
const pdsService = publicConfig.atproto?.service || null
const pdsDid = publicConfig.atproto?.did || null

const form = reactive({
  categoryUri: props.initial?.category?.uri ?? '',
  name: props.initial?.name ?? '',
  description: props.initial?.description ?? '',
  order: props.initial?.order ?? 100,
  image: props.initial?.image?.image,
  imageAlt: props.initial?.image?.alt ?? '',
  aspectRatio: props.initial?.image?.aspectRatio,
  links: (props.initial?.links ?? []).map(l => ({ uri: l.uri, label: l.label ?? '', role: l.role ?? '' })),
})

const localPreviewUrl = ref<string | null>(null)
onBeforeUnmount(() => {
  if (localPreviewUrl.value) URL.revokeObjectURL(localPreviewUrl.value)
})

const submitting = ref(false)
const error = ref<string | null>(null)

const imageUrl = computed(() => {
  if (localPreviewUrl.value) return localPreviewUrl.value
  return pdsBlobUrl(pdsService, pdsDid, form.image)
})

async function onImageChange (e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0]
  if (!file) return

  if (file.size > COMMUNITY_IMAGE_MAX_BYTES) {
    error.value = `That image is ${(file.size / 1_000_000).toFixed(1)} MB; the record format allows 2 MB.`
    return
  }

  if (localPreviewUrl.value) URL.revokeObjectURL(localPreviewUrl.value)
  localPreviewUrl.value = URL.createObjectURL(file)

  const buf = new Uint8Array(await file.arrayBuffer())
  try {
    const uploaded = await $fetch<{
      blob: NonNullable<NonNullable<UsesItemValue['image']>['image']>
      aspectRatio?: { width: number, height: number }
    }>('/api/admin/blobs', {
      method: 'POST',
      body: buf,
      headers: { 'content-type': file.type || 'application/octet-stream' },
    })
    form.image = uploaded.blob
    form.aspectRatio = uploaded.aspectRatio
    if (!form.imageAlt && form.name) form.imageAlt = form.name
  }
  catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
    if (localPreviewUrl.value) {
      URL.revokeObjectURL(localPreviewUrl.value)
      localPreviewUrl.value = null
    }
  }
}

function clearImage () {
  form.image = undefined
  form.aspectRatio = undefined
  form.imageAlt = ''
  if (localPreviewUrl.value) {
    URL.revokeObjectURL(localPreviewUrl.value)
    localPreviewUrl.value = null
  }
}

function addLink () {
  form.links.push({ uri: '', label: '', role: '' })
}

function removeLink (i: number) {
  form.links.splice(i, 1)
}

async function onSubmit () {
  error.value = null
  submitting.value = true
  try {
    const category = (categories.value ?? []).find(c => c.uri === form.categoryUri)
    if (!category) {
      error.value = 'Please pick a category.'
      submitting.value = false
      return
    }
    const categoryRef: StrongRef = { uri: category.uri, cid: category.cid }

    if (form.image && !form.imageAlt) {
      error.value = 'Please describe the image for screen reader users.'
      submitting.value = false
      return
    }

    const links = form.links
      .filter(l => l.uri)
      .map(l => ({
        $type: 'community.lexicon.app.defs#link' as const,
        uri: l.uri,
        ...(l.label ? { label: l.label } : {}),
        ...(l.role ? { role: l.role } : {}),
      }))

    const value: UsesItemValue = {
      category: categoryRef,
      name: form.name,
      ...(form.description ? { description: form.description } : {}),
      order: form.order,
      ...(form.image
        ? {
            image: {
              $type: 'community.lexicon.app.defs#image' as const,
              image: form.image,
              alt: form.imageAlt,
              ...(form.aspectRatio ? { aspectRatio: form.aspectRatio } : {}),
            },
          }
        : {}),
      ...(links.length ? { links } : {}),
      ...(props.initial?.createdAt ? { createdAt: props.initial.createdAt } : { createdAt: new Date().toISOString() }),
    }
    emit('submit', value)
  }
  catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  }
  finally {
    submitting.value = false
  }
}

return (_ctx: any,_cache: any) => {
  const _component_NuxtLink = _resolveComponent("NuxtLink")!

  return (_openBlock(), _createElementBlock("form", {
    class: "flex flex-col gap-4 max-w-2xl",
    onSubmit: _withModifiers(onSubmit, ["prevent"])
  }, [
    (error.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_1, _toDisplayString(error.value), 1))
      : _createCommentVNode("", true),
    _createElementVNode("label", _hoisted_2, [
      _cache[6] || (_cache[6] = _createElementVNode("span", { class: "text-muted" }, [
        _createTextVNode("Category "),
        _createElementVNode("span", { class: "text-red-500" }, "*")
      ], -1)),
      _withDirectives(_createElementVNode("select", {
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(form).categoryUri) = $event)),
        required: "",
        class: "bg-accent px-3 py-2"
      }, [
        _cache[5] || (_cache[5] = _createElementVNode("option", { value: "" }, " - pick a category - ", -1)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(categories) ?? [], (c) => {
          return (_openBlock(), _createElementBlock("option", {
            key: c.uri,
            value: c.uri
          }, _toDisplayString(c.value.title), 9, _hoisted_3))
        }), 128))
      ], 512), [
        [_vModelSelect, _unref(form).categoryUri]
      ])
    ]),
    _createElementVNode("label", _hoisted_4, [
      _cache[7] || (_cache[7] = _createElementVNode("span", { class: "text-muted" }, [
        _createTextVNode("Name "),
        _createElementVNode("span", { class: "text-red-500" }, "*")
      ], -1)),
      _withDirectives(_createElementVNode("input", {
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(form).name) = $event)),
        required: "",
        type: "text",
        class: "bg-accent px-3 py-2"
      }, null, 512), [
        [_vModelText, _unref(form).name]
      ])
    ]),
    _createElementVNode("label", _hoisted_5, [
      _cache[8] || (_cache[8] = _createElementVNode("span", { class: "text-muted" }, "Description", -1)),
      _withDirectives(_createElementVNode("textarea", {
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(form).description) = $event)),
        rows: "3",
        class: "bg-accent px-3 py-2"
      }, null, 512), [
        [_vModelText, _unref(form).description]
      ])
    ]),
    _createElementVNode("label", _hoisted_6, [
      _cache[9] || (_cache[9] = _createElementVNode("span", { class: "text-muted" }, "Order", -1)),
      _withDirectives(_createElementVNode("input", {
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(form).order) = $event)),
        type: "number",
        class: "bg-accent px-3 py-2"
      }, null, 512), [
        [
          _vModelText,
          _unref(form).order,
          void 0,
          { number: true }
        ]
      ]),
      _cache[10] || (_cache[10] = _createElementVNode("span", { class: "text-xs text-muted" }, "Lower numbers render first within a category. Defaults to 100.", -1))
    ]),
    _createElementVNode("div", _hoisted_7, [
      _cache[11] || (_cache[11] = _createElementVNode("span", { class: "text-muted" }, "Links", -1)),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(form).links, (link, i) => {
        return (_openBlock(), _createElementBlock("div", {
          key: i,
          class: "flex gap-2 items-center"
        }, [
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": ($event: any) => ((_unref(form).links[i]!.uri) = $event),
            type: "url",
            placeholder: "https://…",
            class: "bg-accent px-3 py-2 flex-grow",
            "aria-label": `Link ${i + 1} URL`
          }, null, 8, _hoisted_8), [
            [_vModelText, _unref(form).links[i]!.uri]
          ]),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": ($event: any) => ((_unref(form).links[i]!.label) = $event),
            type: "text",
            placeholder: "Label (optional)",
            class: "bg-accent px-3 py-2 w-32",
            "aria-label": `Link ${i + 1} label`
          }, null, 8, _hoisted_9), [
            [_vModelText, _unref(form).links[i]!.label]
          ]),
          _withDirectives(_createElementVNode("select", {
            "onUpdate:modelValue": ($event: any) => ((_unref(form).links[i]!.role) = $event),
            class: "bg-accent px-3 py-2 w-32",
            "aria-label": `Link ${i + 1} role`
          }, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(LINK_ROLES, (role) => {
              return _createElementVNode("option", {
                key: role.value,
                value: role.value
              }, _toDisplayString(role.label), 9, _hoisted_11)
            }), 64))
          ], 8, _hoisted_10), [
            [_vModelSelect, _unref(form).links[i]!.role]
          ]),
          _createElementVNode("button", {
            type: "button",
            class: "text-xs text-muted hover:text-red-500",
            onClick: ($event: any) => (removeLink(i))
          }, " Remove ", 8, _hoisted_12)
        ]))
      }), 128)),
      _createElementVNode("button", {
        type: "button",
        class: "text-xs text-muted hover:text-primary self-start",
        onClick: addLink
      }, " + Add link ")
    ]),
    _createElementVNode("div", _hoisted_13, [
      _createElementVNode("label", _hoisted_14, [
        _cache[12] || (_cache[12] = _createElementVNode("span", { class: "text-muted" }, "Image", -1)),
        _createElementVNode("input", {
          type: "file",
          accept: "image/*",
          class: "text-sm",
          onChange: onImageChange
        }, null, 32)
      ]),
      (_unref(imageUrl))
        ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
            _createElementVNode("img", {
              src: _unref(imageUrl),
              alt: "Item image preview",
              class: "max-h-40 bg-accent object-contain"
            }, null, 8, _hoisted_16),
            _createElementVNode("button", {
              type: "button",
              class: "text-xs text-muted hover:text-red-500 transition-colors",
              onClick: clearImage
            }, " Remove ")
          ]))
        : _createCommentVNode("", true),
      (_unref(form).image)
        ? (_openBlock(), _createElementBlock("label", _hoisted_17, [
            _cache[13] || (_cache[13] = _createElementVNode("span", { class: "text-muted" }, [
              _createTextVNode("Alt text "),
              _createElementVNode("span", { class: "text-red-500" }, "*")
            ], -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(form).imageAlt) = $event)),
              type: "text",
              required: "",
              class: "bg-accent px-3 py-2"
            }, null, 512), [
              [_vModelText, _unref(form).imageAlt]
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_18, [
      _createElementVNode("button", {
        type: "submit",
        disabled: submitting.value,
        class: "bg-primary text-background px-4 py-2 hover:bg-primary/90 transition-colors disabled:opacity-60"
      }, _toDisplayString(submitting.value ? 'Saving…' : (__props.submitLabel ?? 'Save')), 9, _hoisted_19),
      _createVNode(_component_NuxtLink, {
        to: "/admin/uses",
        class: "text-muted hover:text-primary self-center text-sm"
      }, {
        default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
          _createTextVNode(" Cancel ", -1)
        ]))]),
        _: 1
      })
    ])
  ], 32))
}
}

})